import { adjustWeight, Equipment } from '@cf-shared/data-model/equipment';
import { getMinTargetRepsForIntensity, IntensityLevel } from '@cf-shared/data-model/fitness-plan';
import { calculate1RM, estimateWeightForReps, SetStats } from '@cf-shared/data-model/set-stats';
import { SetType } from '@cf-shared/enums';
import { reducedTargetRepsAfterDays, reducedTargetWeightAfterDays } from '@cf-shared/fitness-constants';

/** What a training break does to this session's prescription. */
export enum LayoffOutcome {
  /** the break is too short to change anything */
  None = 'None',
  /** the break is long enough that the path must be tested again (a strength retest) */
  Retest = 'Retest',
  /** the weight is lowered and the rep target stays */
  WeightCut = 'WeightCut',
  /** the weight is held and the rep target is lowered, because a weight cut would not show on the equipment (#3339) */
  RepCut = 'RepCut',
  /** a rep cut was due, but the intensity's rep floor swallowed all of it, so no rep changes and nothing is announced */
  RepCutSwallowedByFloor = 'RepCutSwallowedByFloor',
}

/** Which set the layoff cut was worked out from. */
export enum LayoffEvidence {
  /** today's older rule: a percentage off the last lifted weight, no set's one-rep max involved */
  None = 'None',
  /** the best set of the user's last few sessions, the evidence the #5176 prescription cap uses */
  BestRecentSet = 'BestRecentSet',
  /** the path's last successful set, when no recent set was usable */
  LastStats = 'LastStats',
}

export interface LayoffPrescriptionInput {
  /** days since the exercise was last trained */
  daysSinceLastTrained: number;
  /** the path's last successful set */
  lastStats: SetStats;
  /** the path's rep target, or undefined when the path has none */
  targetReps: number | undefined;
  /** the path's minimum reps, or undefined when the path has none */
  minReps: number | undefined;
  intensityLevel: IntensityLevel;
  /** the equipment attached to the exercise; undefined when none is attached */
  equipment: Equipment | undefined;
  /** the path's stored target weight, which the layoff weight never goes above */
  storedTarget_kg: number | undefined;
  nextWorkoutSetType: SetType | undefined;
  /** the best set of the user's last few sessions, or undefined when there is none or it could not be read */
  bestRecentSet: SetStats | undefined;
  /** today's bodyweight, which the one-rep max is recomputed with */
  bodyWeight_kg: number;
  /** the share of bodyweight the exercise counts, in percent */
  exerciseBw_pc: number;
}

/** Fields every answer that lowers something carries, so the loader can report and store it. */
interface LayoffCutDetails {
  /** which set the cut was worked out from */
  evidence: LayoffEvidence;
  /** the evidence set's one-rep max (bodyweight share included) after the cut; undefined under the
   *  older rule. `concludeSet` derives the other intensity paths' weights from it. */
  cut1RM_net_kg: number | undefined;
}

export type LayoffPrescription =
  | { outcome: LayoffOutcome.None }
  | { outcome: LayoffOutcome.Retest }
  | (LayoffCutDetails & {
    outcome: LayoffOutcome.WeightCut;
    /** the fraction taken off (0.025 or 0.05) — the value the loader stores as `reducedTargetWeight_pc` */
    reduction_pc: number;
    weight_kg: number;
    /** true when no equipment was attached, so the weight could not be put on an equipment grid */
    equipmentMissing: boolean;
  })
  | (LayoffCutDetails & {
    outcome: LayoffOutcome.RepCut;
    /** the weight is held at this value */
    weight_kg: number;
    /** how many reps come off the target (always above 0) */
    repDelta: number;
    targetReps: number;
    /** the lowered minimum reps, or undefined when the path has none */
    minReps: number | undefined;
  })
  | (LayoffCutDetails & {
    outcome: LayoffOutcome.RepCutSwallowedByFloor;
    /** the weight to hold at under the #5617 rule; undefined under the older rule, where the path's
     *  own target weight stays */
    weight_kg: number | undefined;
  });

/** The one-rep max a set implies, bodyweight share included. Recomputed rather than read off the
 *  set, because a stored figure can belong to a weight the user later corrected (#5175). */
function evidence1RM_net_kg(set: SetStats, bodyWeight_kg: number, exerciseBw_pc: number): number {
  return calculate1RM(set.weight_kg, set.reps, bodyWeight_kg, set.bw_pc ?? exerciseBw_pc).estimated1rm_net_kg;
}

/** Round to the nearest loadable weight, or to 2 decimals when no equipment is attached. The
 *  formulas round-trip with floating-point dust (100 x 8 cut 5% comes back as 94.99999999999999),
 *  and a stored target must not carry that. */
function roundLayoffWeight_kg(weight_kg: number, equipment: Equipment | undefined): number {
  if (!equipment) return Math.round(weight_kg * 100) / 100;
  return adjustWeight(weight_kg, 'round', equipment).weight_kg;
}

/** The weight at which `targetReps` is reachable from a one-rep max, never above the stored target,
 *  rounded. Undefined when the one-rep max supports no added weight at all (a pure bodyweight
 *  exercise with nothing added). */
function weightForRepsBelowTarget_kg(args: {
  oneRM_net_kg: number;
  targetReps: number;
  storedTarget_kg: number;
  bodyWeight_kg: number;
  exerciseBw_pc: number;
  equipment: Equipment | undefined;
}): number | undefined {
  const weight_kg = estimateWeightForReps(args.oneRM_net_kg, args.targetReps, args.bodyWeight_kg, args.exerciseBw_pc);
  if (weight_kg === undefined || weight_kg <= 0) return undefined;
  return roundLayoffWeight_kg(Math.min(weight_kg, args.storedTarget_kg), args.equipment);
}

/** The rep cut the day table asks for, or undefined when the intensity's rep floor swallows all of it. */
function repCutFor(input: LayoffPrescriptionInput, targetReps: number): { repDelta: number; targetReps: number; minReps: number | undefined } | undefined {
  const nominalDelta = reducedTargetRepsAfterDays(input.daysSinceLastTrained) ?? 0;
  const floor = getMinTargetRepsForIntensity(input.intensityLevel);
  const newTargetReps = Math.max(floor, targetReps - nominalDelta);
  const repDelta = targetReps - newTargetReps;
  if (repDelta <= 0) return undefined;
  return {
    repDelta,
    targetReps: newTargetReps,
    minReps: typeof input.minReps === 'number' ? Math.max(1, input.minReps - repDelta) : undefined,
  };
}

/**
 * Decide what a training break does to this session's prescription.
 *
 * Pure: the loader applies the answer to the exercise. Since #5617 the cut comes off the one-rep max
 * of the user's best recent set, is run back to the path's own rep target, and is never above the
 * path's stored target. The rules, and when the older rule still applies, are in
 * `design-docs/reactive-deload.md`, "Layoff prescription from the best recent one-rep max (#5617)".
 */
export function decideLayoffPrescription(input: LayoffPrescriptionInput): LayoffPrescription {
  const reduction = reducedTargetWeightAfterDays(input.daysSinceLastTrained);
  // if the break is too short, nothing changes
  if (!reduction) return { outcome: LayoffOutcome.None };
  if (reduction === 'retest') return { outcome: LayoffOutcome.Retest };

  const equipmentMissing = !input.equipment;

  // the #5617 rule needs a rep target this session really asks for, and a stored target to stay
  // under. a RepTest path can keep an old numeric `target_reps` from before its strength test, but
  // its reps come from the intensity, so it keeps the older rule too.
  const targetReps = input.targetReps;
  const storedTarget_kg = input.storedTarget_kg;
  const ruleApplies = input.nextWorkoutSetType !== SetType.RepTest &&
    typeof targetReps === 'number' &&
    typeof storedTarget_kg === 'number' && Number.isFinite(storedTarget_kg);
  if (!ruleApplies) return decideByLastLiftedWeight(input, reduction, equipmentMissing);

  // the evidence: the best recent set, else the last successful set
  let evidenceSet: SetStats;
  let evidence: LayoffEvidence;
  if (input.bestRecentSet) {
    evidenceSet = input.bestRecentSet;
    evidence = LayoffEvidence.BestRecentSet;
  } else if (Number.isFinite(input.lastStats.weight_kg) && input.lastStats.reps > 0) {
    evidenceSet = input.lastStats;
    evidence = LayoffEvidence.LastStats;
  } else {
    return decideByLastLiftedWeight(input, reduction, equipmentMissing);
  }

  const oneRM_net_kg = evidence1RM_net_kg(evidenceSet, input.bodyWeight_kg, input.exerciseBw_pc);
  const cut1RM_net_kg = oneRM_net_kg * (1 - reduction);
  const path = { targetReps, storedTarget_kg, bodyWeight_kg: input.bodyWeight_kg, exerciseBw_pc: input.exerciseBw_pc, equipment: input.equipment };
  const layoffWeight_kg = weightForRepsBelowTarget_kg({ ...path, oneRM_net_kg: cut1RM_net_kg });

  // decide whether the weight is held (and reps come off instead)
  let heldWeight_kg: number | undefined;
  if (layoffWeight_kg === undefined) {
    // nothing can come off an unloaded bodyweight exercise, so hold at the lighter of the plan and
    // what was last lifted (a damaged or missing last lifted weight is ignored, never stored)
    const lastLifted_kg = input.lastStats.weight_kg;
    heldWeight_kg = Number.isFinite(lastLifted_kg) ? Math.min(storedTarget_kg, lastLifted_kg) : storedTarget_kg;
  } else {
    // the weight the user would be asked for with no break. if rounding puts the cut weight at or
    // above it, the cut would not show on the equipment, so a rep comes off instead (#3339)
    const noBreakWeight_kg = weightForRepsBelowTarget_kg({ ...path, oneRM_net_kg });
    if (noBreakWeight_kg === undefined || layoffWeight_kg >= noBreakWeight_kg) {
      heldWeight_kg = layoffWeight_kg;
    }
  }

  if (heldWeight_kg === undefined) {
    return { outcome: LayoffOutcome.WeightCut, reduction_pc: reduction, weight_kg: layoffWeight_kg, equipmentMissing, evidence, cut1RM_net_kg };
  }
  const repCut = repCutFor(input, targetReps);
  // if the rep floor swallows the entire rep cut, no rep changes and nothing is announced (better
  // silence than a misleading "we adjusted X" toast), but the weight is still held where the rule put it
  if (!repCut) {
    return { outcome: LayoffOutcome.RepCutSwallowedByFloor, weight_kg: heldWeight_kg, evidence, cut1RM_net_kg };
  }
  return { outcome: LayoffOutcome.RepCut, weight_kg: heldWeight_kg, ...repCut, evidence, cut1RM_net_kg };
}

/**
 * The older rule: a percentage off the last lifted weight, or a rep cut when that would round back
 * to the same weight (#3339). Still used when the #5617 rule has nothing to work from.
 */
function decideByLastLiftedWeight(input: LayoffPrescriptionInput, reduction: number, equipmentMissing: boolean): LayoffPrescription {
  const noEvidence = { evidence: LayoffEvidence.None, cut1RM_net_kg: undefined };
  const lastWeight_kg = input.lastStats.weight_kg;
  const candidateWeight_kg = olderRuleLayoffWeight_kg(lastWeight_kg, reduction);
  // if equipment is missing, the rounding cannot be checked; the caller reports that
  let weightNudgeRoundsAway = false;
  if (!equipmentMissing) {
    const { weight_kg: roundedCandidate_kg } = adjustWeight(candidateWeight_kg, 'round', input.equipment);
    const { weight_kg: roundedLast_kg } = adjustWeight(lastWeight_kg, 'round', input.equipment);
    weightNudgeRoundsAway = roundedCandidate_kg === roundedLast_kg;
  }

  // if the cut would be invisible and there is a rep target to lower, lower the reps instead
  if (weightNudgeRoundsAway && typeof input.targetReps === 'number') {
    const repCut = repCutFor(input, input.targetReps);
    if (!repCut) return { outcome: LayoffOutcome.RepCutSwallowedByFloor, weight_kg: undefined, ...noEvidence };
    return { outcome: LayoffOutcome.RepCut, weight_kg: lastWeight_kg, ...repCut, ...noEvidence };
  }

  return { outcome: LayoffOutcome.WeightCut, reduction_pc: reduction, weight_kg: candidateWeight_kg, equipmentMissing, ...noEvidence };
}

/** The older rule's weight: `reduction_pc` taken off the last lifted weight. */
export function olderRuleLayoffWeight_kg(lastLiftedWeight_kg: number, reduction_pc: number): number {
  return lastLiftedWeight_kg - lastLiftedWeight_kg * reduction_pc;
}

/** One of the exercise's OTHER intensity paths, as the first set's conclusion sees it. */
export interface OtherIntensityPath {
  target_weight_kg?: number;
  target_reps?: number;
  nextWorkoutSetType?: SetType;
  lastStats?: SetStats;
}

/**
 * The weight a first set's conclusion writes to one of the exercise's OTHER intensity paths after a
 * break, or undefined to leave that path alone. Only the weight is ever written; a path's rep
 * target is never touched, and only the active path gets the rep cut.
 *
 * - A path with no target weight or no last lifted weight is left alone (the #916 workaround).
 * - A path the #5617 rule can judge gets `layoffWeightForPath_kg` from the active path's cut
 *   one-rep max, in both the weight-cut and the rep-cut branch.
 * - A path it cannot judge (a RepTest, or no numeric rep target), or any path when the active path
 *   fell back to the older rule, gets the older rule's weight in the weight-cut branch
 *   (`reduction_pc` set) and is left alone in the rep-cut branch, exactly as before #5617.
 */
export function otherIntensityLayoffWeight_kg(path: OtherIntensityPath, args: {
  /** the active path's cut one-rep max; undefined when the active path used the older rule */
  cut1RM_net_kg: number | undefined;
  /** the weight cut's fraction; undefined in the rep-cut branch */
  reduction_pc: number | undefined;
  bodyWeight_kg: number;
  exerciseBw_pc: number;
  equipment: Equipment | undefined;
}): number | undefined {
  // just in case something is not initialized yet (workaround for #916)
  if (!path.target_weight_kg || !path.lastStats?.weight_kg) return undefined;
  const ruleApplies = args.cut1RM_net_kg !== undefined &&
    path.nextWorkoutSetType !== SetType.RepTest &&
    typeof path.target_reps === 'number';
  if (ruleApplies) {
    return layoffWeightForPath_kg({
      cut1RM_net_kg: args.cut1RM_net_kg,
      targetReps: path.target_reps,
      storedTarget_kg: path.target_weight_kg,
      lastLiftedWeight_kg: path.lastStats.weight_kg,
      bodyWeight_kg: args.bodyWeight_kg,
      exerciseBw_pc: args.exerciseBw_pc,
      equipment: args.equipment,
    });
  }
  if (args.reduction_pc && args.reduction_pc > 0) {
    return olderRuleLayoffWeight_kg(path.lastStats.weight_kg, args.reduction_pc);
  }
  return undefined;
}

/**
 * The layoff weight for one of the exercise's OTHER intensity paths (#5617): the same cut one-rep
 * max, run back to that path's own rep target, never above that path's stored target, rounded. When
 * it supports no added weight at all, the lighter of the stored target and the last lifted weight,
 * as for the active path.
 */
export function layoffWeightForPath_kg(args: {
  cut1RM_net_kg: number;
  targetReps: number;
  storedTarget_kg: number;
  lastLiftedWeight_kg?: number;
  bodyWeight_kg: number;
  exerciseBw_pc: number;
  equipment: Equipment | undefined;
}): number {
  const weight_kg = weightForRepsBelowTarget_kg({ ...args, oneRM_net_kg: args.cut1RM_net_kg });
  if (weight_kg !== undefined) return weight_kg;
  // a damaged or missing last lifted weight is ignored, never stored
  const lastLifted_kg = args.lastLiftedWeight_kg;
  if (typeof lastLifted_kg !== 'number' || !Number.isFinite(lastLifted_kg)) return args.storedTarget_kg;
  return Math.min(args.storedTarget_kg, lastLifted_kg);
}
